/* FERAL CARD: the quiet line on the product page and in the bag.
   "Counts towards your FERAL CARD", or for a signed-in member whose card we know,
   "This gets you £31 closer to your next £5" / "This gets you your next £5 back".

   Counted the way the engine counts: what you pay for clothes, without VAT and
   delivery, in GBP pence (score.gbp_per_pct per 1%), converted from the shopper's
   currency at the programme's own display rate (score.display_per_pct), rounded
   down so it never promises more than the order earns. The crossing maths is
   HFRewards (hf-rewards-core.js), the same pure core the pages and engine use.
   The engine counts the paid order, not this line.

   Source: ~/landers/feral-card/line/feral-card-line.js. The snippet that feeds it,
   snippets/feral-card-line.liquid, is generated from ~/loyalty/rules.v2.json by
   feral-card/line/build.mjs. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory(require('../../how-feral/assets/hf-rewards-core.js'));
  else root.FeralCardLine = factory(root.HFRewards);
})(typeof globalThis !== 'undefined' ? globalThis : this, function (H) {
  'use strict';
  /* Standard VAT by destination, in % (SOURCED: each country's standard rate, 2026), the FERAL
     app's table (src/lib/loyalty-core.ts `withoutVat`). A bag with no country yet is priced as
     the UK, the home market. NO and CH are JUDGED in, as in the app: taking out tax that was
     never charged under-promises; leaving in tax that was charged over-promises. */
  var VAT = {
    GB: 20, AT: 20, BE: 21, BG: 20, HR: 25, CY: 19, CZ: 21, DK: 25, EE: 24, FI: 25.5, FR: 20, DE: 19, GR: 24, HU: 27, IE: 23, IT: 22, LV: 21,
    LT: 21, LU: 17, MT: 18, NL: 21, PL: 23, PT: 23, RO: 21, SK: 23, SI: 22, ES: 21, SE: 25, NO: 25, CH: 8.1
  };
  var GENERIC = 'Counts towards your FERAL CARD';
  var LEVELS = ['house_trained', 'stray', 'wild', 'unhinged', 'feral'];

  function whole(n) { return typeof n === 'number' ? n : typeof n === 'string' && /^\d{1,15}$/.test(n) ? Number(n) : NaN; }
  function count(n) { var v = whole(n); return Number.isSafeInteger(v) && v >= 0 ? v : null; }

  /* The member's card as the engine mirrors it into customer metafields, validated as the
     pages do. A signed-in customer who has never ordered and has no metafields is the zero
     state; a buyer whose metafields are missing is unknown, and gets the plain line. */
  function memberFrom(m, levels) {
    if (!m || !m.logged_in) return null;
    var keys = (levels || []).map(function (l) { return l.key; });
    var net = count(m.net), orders = count(m.orders), cursor = count(m.cursor), level = keys.indexOf(m.level);
    if (net != null && orders != null && cursor != null && level >= 0) return { net_minor: net, orders: orders, level: level, drop_cursor: cursor };
    var blank = [m.net, m.orders, m.cursor, m.level].every(function (v) { return v == null || v === ''; });
    if (blank && count(m.orders_count) === 0) return { net_minor: 0, orders: 0, level: 0, drop_cursor: 0 };
    return null;
  }

  function money(iso) {
    var f;
    try { f = new Intl.NumberFormat('en-GB', { style: 'currency', currency: iso, minimumFractionDigits: 0, maximumFractionDigits: 0 }); } catch (e) { return null; }
    return function (n) { return f.format(n); };
  }

  /* o: { rules, member (already validated, or null), amountMinor (hundredths of the shop's
     currency, as Liquid prints money), iso, country, taxesIncluded }. Returns { kind, text }:
     kind 'none' (nothing to say: no amount), 'generic', 'closer' or 'reward'. */
  function lineFor(o) {
    var r = o && o.rules, amount = o && o.amountMinor;
    if (!(typeof amount === 'number' && amount > 0)) return { kind: 'none', text: '' };
    var generic = { kind: 'generic', text: GENERIC };
    var m = o.member, iso = o.iso;
    if (!r || !m || !H) return generic;
    var value = r.drops && r.drops.value && r.drops.value[iso], per = r.display_per_pct && r.display_per_pct[iso], fmt = money(iso);
    if (!(value > 0) || !(per > 0) || !fmt) return generic;
    var vat = o.taxesIncluded ? (VAT[o.country || 'GB'] || 0) : 0;
    var local = amount / 100 / (1 + vat / 100), shown = Math.floor(local + 1e-9);
    if (shown < 1) return generic;
    var pence = Math.floor(local / per * r.gbp_per_pct * 100 + 1e-9);
    var p;
    try { p = H.project(r, m, pence); } catch (e) { return generic; }
    if (p.backs.length) {
      var sum = p.backs.reduce(function (n, x) { return n + x; }, 0) * value;
      return { kind: 'reward', text: p.backs.length === 1 ? 'This gets you your next ' + fmt(sum) + ' back' : 'This gets you ' + fmt(sum) + ' back' };
    }
    var s = H.state(r, m), next = value * H.backAt(r, s.drop_cursor + 1, s.level, Math.max(s.orders, 1));
    return { kind: 'closer', text: 'This gets you ' + fmt(shown) + ' closer to your next ' + fmt(next) };
  }

  /* ── The page. Every [data-feral-card-line] node is filled from the data the snippet printed
     beside it; the cart drawer re-renders its HTML on every change, so new nodes are watched for. */
  function boot() {
    if (typeof document === 'undefined' || window.__feralCardLine) return;
    window.__feralCardLine = true;
    function data(node) {
      var s = node.querySelector('script[data-feral-card-data]') || document.querySelector('script[data-feral-card-data]');
      try { return JSON.parse(s.textContent); } catch (e) { return null; }
    }
    /* Preview themes only: ?fc_member=<net_minor>,<orders>,<level_key>,<cursor> paints a member
       for screenshots. Fails closed on the live theme and on any malformed value. */
    function fixture(d) {
      if (!d || d.role === 'main' || !d.role) return null;
      var v = new URLSearchParams(location.search).get('fc_member') || '';
      var m = /^(\d{1,12}),(\d{1,6}),(house_trained|stray|wild|unhinged|feral),(\d{1,6})$/.exec(v);
      return m ? { logged_in: true, net: m[1], orders: m[2], level: m[3], cursor: m[4] } : null;
    }
    function paint(node) {
      var d = data(node); if (!d || !H) return;
      var amount = whole(node.getAttribute('data-amount'));
      var member = memberFrom(fixture(d) || d.member, d.rules && d.rules.levels);
      var out = lineFor({ rules: d.rules, member: member, amountMinor: amount, iso: d.iso, country: d.country, taxesIncluded: !!d.taxes });
      node.hidden = out.kind === 'none';
      var a = node.querySelector('a'), say = a && a.querySelector('span');
      if (!say) return;
      if (out.kind === 'generic' || out.kind === 'none') say.innerHTML = 'Counts towards your <b>FERAL CARD</b>';
      else say.textContent = out.text;
      a.removeAttribute('aria-label');
      node.setAttribute('data-kind', out.kind);
    }
    /* The product page: the price changes with the size picked. feral-product.js writes the new
       price into [data-fp-price-current] and the variant id into [data-fp-variant-id]; the price
       is read from the section's own variants JSON, never parsed from formatted text. */
    function followVariant(node) {
      var scope = node.parentNode;
      while (scope && scope !== document.body && !(scope.querySelector && scope.querySelector('[data-fp-variants]'))) scope = scope.parentNode;
      if (!scope || scope === document.body) return;
      var price = scope.querySelector('[data-fp-price-current]'), id = scope.querySelector('[data-fp-variant-id]'), list = scope.querySelector('[data-fp-variants]');
      if (!price || !id || !list || node._fclFollow) return;
      node._fclFollow = true;
      var variants = []; try { variants = JSON.parse(list.textContent); } catch (e) {}
      new MutationObserver(function () {
        var v = variants.find(function (x) { return String(x.id) === String(id.value); });
        if (v && typeof v.price === 'number') { node.setAttribute('data-amount', String(v.price)); paint(node); }
      }).observe(price, { childList: true, characterData: true, subtree: true });
    }
    function scan(rootNode) {
      (rootNode.querySelectorAll ? rootNode.querySelectorAll('[data-feral-card-line]') : []).forEach(function (n) {
        paint(n); if (n.getAttribute('data-context') === 'product') followVariant(n);
      });
    }
    function start() {
      scan(document);
      new MutationObserver(function (records) {
        records.forEach(function (r) { r.addedNodes.forEach(function (n) { if (n.nodeType !== 1) return; if (n.matches('[data-feral-card-line]')) paint(n); else scan(n); }); });
      }).observe(document.body, { childList: true, subtree: true });
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  }
  if (typeof window !== 'undefined') boot();
  return Object.freeze({ lineFor: lineFor, memberFrom: memberFrom, VAT: VAT, GENERIC: GENERIC, LEVELS: LEVELS });
});
